import { Box } from '@deckee/deck-hand';
import React, { FC } from 'react';
import styled, { useTheme } from 'styled-components';
import { ReactSvg } from '../../../shared/vector-images';
import type { VectorImage } from '../../../shared/vector-images/types';
import {
  SIDEBAR_BADGE_RIGHT,
  SIDEBAR_BADGE_SIZE,
  SIDEBAR_BADGE_TOP,
  SIDEBAR_ICON_SIZE,
  SIDEBAR_ITEM_HEIGHT,
  SIDEBAR_ITEM_RADIUS,
  SIDEBAR_TRANSITION,
} from '../sidebarConstants';

const SidebarButton = styled(Box).attrs({ as: 'button', type: 'button' })<{
  $active?: boolean;
}>`
  /* Button reset that leaves the Box props (padding, bg, colour, size) intact */
  appearance: none;
  border: 0;
  margin: 0;
  font: inherit;
  text-align: left;
  box-sizing: border-box;
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${({ theme, $active }) =>
      $active ? theme.colors.primarylighter : theme.colors.lightgrey};
  }

  &:disabled {
    cursor: default;
    opacity: 0.4;
  }
`;

const ItemLabel = styled(Box)<{ $collapsed: boolean }>`
  opacity: ${({ $collapsed }) => ($collapsed ? 0 : 1)};
  transition: opacity ${SIDEBAR_TRANSITION};
`;

interface SidebarItemProps {
  label: string;
  active: boolean;
  collapsed: boolean;
  disabled?: boolean;
  onClick: () => void;
  icon?: VectorImage;
  badgeCount?: number;
  /** Custom icon content; when undefined, `icon` is rendered instead */
  children?: React.ReactNode;
}

const SidebarItem: FC<SidebarItemProps> = ({
  label,
  active,
  collapsed,
  disabled,
  onClick,
  icon,
  badgeCount,
  children,
}) => {
  const theme = useTheme();

  return (
    <SidebarButton
      $active={active}
      disabled={disabled}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
      display="flex"
      alignItems="center"
      width="100%"
      height={SIDEBAR_ITEM_HEIGHT}
      mb={1}
      px={2.5}
      borderRadius={SIDEBAR_ITEM_RADIUS}
      color={active ? 'primary' : 'black'}
      bg={active ? 'primarylighter' : 'transparent'}
    >
      <Box
        position="relative"
        display="flex"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        width={SIDEBAR_ICON_SIZE}
        height={SIDEBAR_ICON_SIZE}
        mr={2.5}
      >
        {children ?? (
          <ReactSvg
            toDisplay={icon}
            options={{
              fillColor: active ? theme.colors.primary : theme.colors.black,
            }}
            width={SIDEBAR_ICON_SIZE}
            height={SIDEBAR_ICON_SIZE}
          />
        )}
        {badgeCount ? (
          <Box
            position="absolute"
            top={SIDEBAR_BADGE_TOP}
            right={SIDEBAR_BADGE_RIGHT}
            color="white"
            bg="primary"
            fontSize={0}
            fontWeight={500}
            borderRadius={SIDEBAR_BADGE_SIZE}
            px={1}
            minWidth={SIDEBAR_BADGE_SIZE}
            lineHeight={SIDEBAR_BADGE_SIZE}
            textAlign="center"
          >
            {badgeCount}
          </Box>
        ) : null}
      </Box>
      <ItemLabel $collapsed={collapsed} fontSize={2} fontWeight={500}>
        {label}
      </ItemLabel>
    </SidebarButton>
  );
};

SidebarItem.defaultProps = {
  disabled: false,
  icon: undefined,
  badgeCount: 0,
  children: undefined,
};

export default SidebarItem;
